import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import React, { useState } from "react";
import ClientBrainStore from "stores/ClientBrainStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";
import { formatShortDate } from "../clientBrainUtils";

type Props = {
    store: ClientBrainStore;
    canEdit?: boolean;
};

const ClientGoalsSection: React.FC<Props> = observer(
    ({ store, canEdit = false }) => {
        const theme = useTheme();
        const goals = store.organizationContext.goalsAndPriorities;

        const [editing, setEditing] = useState(false);
        const [text, setText] = useState("");
        const [saving, setSaving] = useState(false);

        const startEdit = () => {
            setText(goals?.value ?? "");
            setEditing(true);
        };

        const save = async () => {
            setSaving(true);
            try {
                await store.saveGoals(text);
                setEditing(false);
            } finally {
                setSaving(false);
            }
        };

        return (
            <ClientBrainSectionCard
                title="Client Goals & Priorities"
                subtitle="Input what this client is trying to achieve, in their own words. Include general information about this client, such as competitive therapies, focus areas, and priorities."
                headerAction={
                    canEdit && !store.isLoading && !editing ? (
                        <AcxButton
                            color="primary"
                            variant="text"
                            onClick={startEdit}
                        >
                            Edit
                        </AcxButton>
                    ) : undefined
                }
            >
                {store.isLoading ? (
                    <Skeleton variant="rounded" height={96} />
                ) : editing ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <AcxMainTextField
                            id="client-brain-goals"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            multiline
                            rows={4}
                            rowsMax={10}
                            fullWidth
                        />
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                            }}
                        >
                            <AcxButton
                                color="white"
                                onClick={() => setEditing(false)}
                                disabled={saving}
                            >
                                Cancel
                            </AcxButton>
                            <AcxButton
                                color="primary"
                                onClick={save}
                                loading={saving}
                            >
                                Save
                            </AcxButton>
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.gray[200]}`,
                            borderRadius: "6px",
                            padding: theme.spacing(2),
                            backgroundColor: theme.palette.gray[50],
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: goals?.value
                                    ? theme.palette.gray[700]
                                    : theme.palette.gray[400],
                                whiteSpace: "pre-wrap",
                                fontStyle: goals?.value ? "normal" : "italic",
                            }}
                        >
                            {goals?.value ||
                                "No goals or priorities added yet."}
                        </Typography>
                        {(goals?.modifiedBy || goals?.modifiedOn) && (
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: theme.palette.gray[400],
                                    marginTop: theme.spacing(1.5),
                                }}
                            >
                                Last updated by {goals?.modifiedBy} ·{" "}
                                {formatShortDate(goals?.modifiedOn)}
                            </Typography>
                        )}
                    </Box>
                )}
            </ClientBrainSectionCard>
        );
    },
);

export default ClientGoalsSection;
